import "./workView.scss";

import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React from "react";

import FormTextField from "../../components/form/FormTextField";
import Icon from "../../components/interface/Icon";
import PubSubTopics from "../../constants/pubSubTopics";
import Dimensions, { scalePixelsToFontSize } from "../../constants/styles/dimensions";
import { PropTypeShapes } from "../../types";
import { chooseValueForIos, disableWhenIos } from "../../utils/codeSplit";
import preprocessorQuery from "../../utils/preprocessorQuery";

const makeButton = (icon, onClick, isActive, tooltip) => (
  <div key={icon} className="workViewHeaderButton" onClick={onClick} title={tooltip}>
    <svg className={`icon ${isActive ? "iconOnActive" : "workviewHeaderIcon"}`}>
      <use xlinkHref={`#${icon}`} />
    </svg>
  </div>
);

const isReadonly = preprocessorQuery.isReadonly();

class WorkViewHeader extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      value: this.props.label,
      initialValue: this.props.label,
      isEditing: false
    };

    this.onDoubleClick = this.onDoubleClick.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.updateLabel = this.updateLabel.bind(this);
    this.onAltModifierKeyDown = this.onAltModifierKeyDown.bind(this);
    this.onAltModifierKeyUp = this.onAltModifierKeyUp.bind(this);
  }

  componentDidMount() {
    this._pubSubAltModifierKeyDownToken = PubSub.subscribe(PubSubTopics.altModifierKeyDown, this.onAltModifierKeyDown);
    this._pubSubAltModifierKeyUpToken = PubSub.subscribe(PubSubTopics.altModifierKeyUp, this.onAltModifierKeyUp);
  }

  componentDidUpdate() {
    this.updateLabel();
  }

  componentWillUnmount() {
    PubSub.unsubscribe(this._pubSubAltModifierKeyDownToken);
    PubSub.unsubscribe(this._pubSubAltModifierKeyUpToken);
  }

  onAltModifierKeyDown() {
    this.setState({
      altModifierKeyPressed: true
    });
  }

  onAltModifierKeyUp() {
    this.setState({
      altModifierKeyPressed: false
    });
  }

  onChange(value) {
    this.props.onEditTitle(value);
  }

  onBlur() {
    this.setState({ isEditing: false });
  }

  onDoubleClick() {
    if (!isReadonly && this.props.onEditTitle) {
      this.setState({ isEditing: true });
    }
  }

  updateLabel = () => {
    if (this.state.initialValue !== this.props.label) {
      this.setState({
        value: this.props.label,
        initialValue: this.props.label
      });
    }
  };

  getSubLabelIcon() {
    const { subLabelIcon } = this.props;
    return subLabelIcon ? <Icon icon={subLabelIcon} /> : null;
  }

  getSubLabel() {
    const { subLabel, onSubLabelClick } = this.props;
    return subLabel ? (
      <span className=" workViewHeaderSubLabels" onClick={onSubLabelClick ? onSubLabelClick : null}>
        {subLabel}
      </span>
    ) : null;
  }

  getSubTitleElements() {
    const separator = "";
    // this.props.subLabelIcon || this.props.subLabel ? "| " : "";
    return (
      <div className="workViewHeaderSubTitle">
        {separator}
        {this.getSubLabelIcon()}
        {this.getSubLabel()}
      </div>
    );
  }

  render() {
    let buttons = [];
    if (this.props.showMinimize) {
      buttons.push(
        makeButton(
          this.props.isMinimized ? "maximizeWindow" : "minimizeWindow",
          this.props.onMinimize,
          this.props.isActive,
          this.props.isMinimized ? "Expand work view" : "Minimize work view"
        )
      );
    }

    if (this.props.showSplitVertical && this.state.altModifierKeyPressed) {
      buttons.push(
        makeButton("workviewSplitVertical", this.props.onSplitVertical, this.props.isActive, "Split vertically")
      );
    } else if (this.props.showSplitHorizontal) {
      buttons.push(
        makeButton("workviewSplitHorizontal", this.props.onSplitHorizontal, this.props.isActive, "Split horizontally")
      );
    }
    if (this.props.showClose) {
      // buttons.push(makeButton("workviewPopout", this.props.onPopout));
      buttons.push(makeButton("workviewClose", this.props.onClose, this.props.isActive, "Close work view"));
    }

    const SCALED_WORKVIEW_HEADER_HEIGHT = scalePixelsToFontSize(
      Dimensions.WORKVIEW_HEADER_HEIGHT,
      this.props.globalFontSize
    );

    let style = {
      width: `${this.props.containerRect.width}px`,
      left: `${this.props.containerRect.left}px`,
      top: `${this.props.containerRect.top}px`,
      height: `${SCALED_WORKVIEW_HEADER_HEIGHT}px`
    };

    return (
      <div className="workViewHeaderWrap">
        <div className={`workViewHeader ${this.props.labelColor}`} style={style}>
          {this.state.isEditing ? (
            <div className="workViewHeaderTitleContainer">
              <FormTextField onChange={this.onChange} onBlur={this.onBlur} value={this.props.label} autoFocus={true} />
            </div>
          ) : (
            <div className="workViewHeaderTitleContainer">
              <div
                className="workViewHeaderTitle"
                onDoubleClick={disableWhenIos(this.onDoubleClick)}
                onClick={chooseValueForIos(this.onDoubleClick, null)}
              >
                {this.props.onEditTitle ? chooseValueForIos(<Icon icon="toolbarEdit" color="iconMono" />, null) : null}
                {this.props.label}
              </div>
              {this.getSubTitleElements()}
            </div>
          )}
          {buttons}
        </div>
      </div>
    );
  }
}

WorkViewHeader.propTypes = {
  label: PropTypes.string.isRequired,
  labelColor: PropTypes.string,
  subLabelIcon: PropTypes.string,
  subLabel: PropTypes.string,
  onSubLabelClick: PropTypes.func,
  showClose: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onPopout: PropTypes.func,
  showSplitHorizontal: PropTypes.bool.isRequired,
  showSplitVertical: PropTypes.bool.isRequired,
  showMinimize: PropTypes.bool.isRequired,
  isMinimized: PropTypes.bool,
  onMinimize: PropTypes.func,
  onSplitHorizontal: PropTypes.func,
  onSplitVertical: PropTypes.func,
  onFilter: PropTypes.func,
  onEditTitle: PropTypes.func,
  isActive: PropTypes.bool,
  globalFontSize: PropTypes.number.isRequired,

  // Passed in directly by workView
  containerRect: PropTypeShapes.flatRect.isRequired
};

export default WorkViewHeader;
